import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { race, timer } from 'rxjs';
import {
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { APP_ROUTES } from '@cpd-app/core-module/constants/routes';
import { AccountCreationPromptService } from '@cpd-app/core-module/services/auth/account-creation-prompt.service';
import { MessageService } from '@cpd-app/core-module/services/messages/message.service';
import { NavigationService } from '@cpd-app/core-module/services/navigation/navigation.service';
import {
  ReturnLinkClaimOutcome,
  ReturnLinkDataService
} from '@cpd-app/core-module/services/return-link/return-link-data.service';
import { returnLinkMemory } from '@cpd-app/core-module/services/return-link/return-link-memory';

import { CognitoAuthActions, CognitoAuthFacade } from '../cognito-auth';
import { LoaderFacade } from '../loader';
import * as AuthActions from './auth.actions';
import { AuthFacade } from './auth.facade';

/** How long to wait for session start-up before acting on the link anyway. */
const SETTLE_MS = 5000;

/*
 * The home page by its own path. APP_ROUTES.Home is '/', which
 * NavigationService.setDirectLink ignores, so start-up's direct-link jump
 * would then move the page (and close the dialog) after this lands here.
 */
const HOME_PAGE = '/home';

/** Approved by Jackson, 13 Oct 2026 (AUTH.9 section 10). */
const CLAIM_MESSAGES: Partial<Record<ReturnLinkClaimOutcome, string>> = {
  already_claimed:
    'This report is already linked to an account. Sign in with that email to edit it.',
  failed: "We couldn't add your email just now. Please try again.",
  not_live: "We couldn't add your email just now. Please try again.",
  not_signed_in: "We couldn't add your email just now. Please try again.",
  staff:
    'This report is already linked to an account. Sign in with that email to edit it.'
};

export const returnTokenFromUrl = (href = window.location.href): string | null => {
  try {
    return new URL(href).searchParams.get('returnToken')?.trim() || null;
  } catch {
    return null;
  }
};

/**
 * AUTH.9. Old goals report links (`/goals-report?returnToken=<uuid>`).
 *
 * These links used to sign the holder in as the member. Now (Jackson, 13 Oct
 * 2026, spec section 5):
 *
 *  - VIEW opens the read-only shared report at /shared-report/<token>, with a
 *    banner offering sign-in or "add your email";
 *  - SIGN_IN and INVALID open the "This link has expired" card.
 *
 * Session start-up runs as usual alongside, so a visitor who is already
 * signed in keeps their own session. Nothing here stores a share token: that
 * is what made a refused link look like a successful one.
 *
 * Registered right after AuthEffects, ahead of CognitoAuthEffects, whose
 * ngrxOnInitEffects dispatches processReturnSession at start-up.
 */
@Injectable()
export class ReturnLinkEffects {
  public openOnLanding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.processReturnSession),
      tap(() =>
        this.loaderFacade.showLoaderUntil(
          AuthActions.processReturnSessionCompleted.type
        )
      ),
      switchMap(() => {
        const token = returnTokenFromUrl();
        const settled$ = race(
          this.authFacade.isSessionLoaded$.pipe(filter(Boolean), take(1)),
          timer(SETTLE_MS)
        ).pipe(take(1));

        return this.data.open(token ?? '').pipe(
          switchMap((opening) => settled$.pipe(map(() => opening))),
          map((opening) => {
            if (opening.outcome === 'VIEW' && token) {
              returnLinkMemory.rememberView({
                token,
                canClaim: opening.canClaim,
                renewed: opening.renewed
              });
              const target = `${APP_ROUTES.SharedReport}/${token}`;
              this.navigationService.setDirectLink(target);
              this.router.navigateByUrl(target, { replaceUrl: true });
            } else {
              this.navigationService.setDirectLink(HOME_PAGE);
              this.router
                .navigateByUrl(HOME_PAGE, { replaceUrl: true })
                .then(() => this.prompt.openAt('returnExpired'));
              returnLinkMemory.rememberPending({ mode: 'signin' });
            }

            return AuthActions.processReturnSessionCompleted();
          })
        );
      })
    )
  );

  /**
   * Kick off the normal start-up, which processReturnSession used to replace.
   * Separate from the effect above so start-up never waits on the link.
   */
  public startSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.processReturnSession),
      map(() => CognitoAuthActions.checkAuthenticationStatus())
    )
  );

  /**
   * After any sign-in completes: finish what the banner or the expired card
   * asked for. A claim attaches the new email to the old record and then
   * re-links the session, which lands the member in that record.
   */
  public afterSignIn$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.getSessionSuccess),
        withLatestFrom(this.cognitoAuthFacade.isAuthenticated$),
        filter(([, isAuthenticated]) => !!isAuthenticated),
        map(() => returnLinkMemory.pending()),
        filter(Boolean),
        tap(() => returnLinkMemory.clearPending()),
        switchMap((pending) => {
          /*
           * Set the direct link as well as navigating. Start-up jumps to the
           * direct link 200ms after the session lands (AuthEffects
           * .sessionSuccess$), and that jump used to win: on dev, 13 Oct, a
           * sign-in from the expired card ended on build-your-target.
           */
          this.navigationService.setDirectLink(APP_ROUTES.GoalsReport);

          if (pending.mode !== 'claim') {
            return this.router.navigateByUrl(APP_ROUTES.GoalsReport);
          }

          return this.data.claim(pending.token).pipe(
            tap((outcome) => {
              if (outcome === 'claimed') {
                // Re-link: sign-in maps by email, which now names the old
                // record, so this returns that record's session. The report
                // opens once that session is in (AuthEffects.sessionSuccess$
                // goes to the direct link), not before, so it never shows the
                // empty row the sign-in started from.
                this.navigationService.setDirectLink(APP_ROUTES.GoalsReport);
                this.store.dispatch(CognitoAuthActions.cognitoCallback());
                return;
              }

              if (outcome === 'merged') {
                this.router.navigateByUrl(APP_ROUTES.GoalsReport);
                return;
              }

              this.messages.createMessageDialog(
                'Add your email',
                CLAIM_MESSAGES[outcome] ?? CLAIM_MESSAGES.failed
              );
            })
          );
        })
      ),
    { dispatch: false }
  );

  public constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly data: ReturnLinkDataService,
    private readonly authFacade: AuthFacade,
    private readonly cognitoAuthFacade: CognitoAuthFacade,
    private readonly loaderFacade: LoaderFacade,
    private readonly navigationService: NavigationService,
    private readonly prompt: AccountCreationPromptService,
    private readonly messages: MessageService,
    private readonly router: Router
  ) {}
}
